import i18next, { type i18n as I18nInstance } from 'i18next';
import {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from './config.js';
import bgCommon from './locales/bg/common.json' with { type: 'json' };
import deCommon from './locales/de/common.json' with { type: 'json' };
// Statically import all locale bundles. Keeping them here (not in separate
// async chunks) lets the i18n package stay self-contained; apps that need
// code-splitting can lazy-load additional namespaces by supplying `resources`
// to createI18n and deferring the import themselves.
import enCommon from './locales/en/common.json' with { type: 'json' };
import frCommon from './locales/fr/common.json' with { type: 'json' };

/** Resource bundle for a single locale (namespace → translation map). */
export type LocaleResources = Record<string, Record<string, unknown>>;

/** Full resource map keyed by locale code. */
export type ResourceMap = Partial<Record<SupportedLocale, LocaleResources>>;

export interface CreateI18nOptions {
  /** Active locale. Defaults to DEFAULT_LOCALE ('en'). */
  locale?: SupportedLocale;
  /**
   * Optional additional resources to merge on top of the bundled ones.
   * Useful for app-level namespace extensions or lazy-loaded chunks.
   */
  resources?: ResourceMap;
}

/** Bundled resources for all supported locales. */
const BUNDLED_RESOURCES: Record<SupportedLocale, LocaleResources> = {
  en: { common: enCommon as Record<string, unknown> },
  bg: { common: bgCommon as Record<string, unknown> },
  de: { common: deCommon as Record<string, unknown> },
  fr: { common: frCommon as Record<string, unknown> },
};

/**
 * Determines the current runtime environment.
 * - 'test'       → Vitest sets import.meta.env.MODE = 'test'
 * - 'production' → Vite production build sets MODE = 'production'
 * - 'development'→ everything else (local dev server)
 */
function resolveEnv(): 'test' | 'production' | 'development' {
  // import.meta.env is always defined in Vite/Vitest ESM contexts.
  // We access MODE via bracket notation so TypeScript does not narrow it away.
  const mode: string | undefined = import.meta.env['MODE'];
  if (mode === 'test') return 'test';
  if (mode === 'production') return 'production';
  return 'development';
}

/**
 * Creates and initialises a configured i18next instance.
 *
 * - In 'test' and 'production' environments a missing translation key THROWS
 *   so that CI fails and build pipelines surface gaps early.
 * - In 'development' a console.warn is emitted instead so you can still work
 *   while adding new keys.
 */
export function createI18n(options: CreateI18nOptions = {}): I18nInstance {
  const { locale = DEFAULT_LOCALE, resources: extraResources } = options;
  const env = resolveEnv();

  // Merge bundled + caller-supplied resources
  const mergedResources: Record<SupportedLocale, LocaleResources> = {
    ...BUNDLED_RESOURCES,
  };

  if (extraResources) {
    for (const loc of SUPPORTED_LOCALES) {
      const extra = extraResources[loc];
      if (extra) {
        mergedResources[loc] = { ...mergedResources[loc], ...extra };
      }
    }
  }

  const instance = i18next.createInstance();

  instance.init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: DEFAULT_NAMESPACE,
    resources: mergedResources,
    interpolation: {
      // React already escapes values; disable double-escaping
      escapeValue: false,
    },
    // Disable the default missing-key log; we handle it in missingKeyHandler
    missingKeyNoValueFallbackToKey: true,
    parseMissingKeyHandler: (key: string) => key,
    saveMissing: env !== 'development',
    missingKeyHandler: (
      _lngs: readonly string[],
      _ns: string,
      key: string,
      _fallbackValue: string,
    ) => {
      const message = `[i18n] Missing translation key: "${key}"`;
      if (env === 'test' || env === 'production') {
        throw new Error(message);
      } else {
        console.warn(message);
      }
    },
  });

  return instance;
}

/**
 * Asserts that every locale has exactly the same set of leaf keys as the
 * reference locale ('en').
 *
 * Call this from your test suite to enforce key parity across all locales
 * before a build lands.
 *
 * @param locales - Subset of locales to check. Defaults to all SUPPORTED_LOCALES.
 * @throws {Error} listing every missing or extra key per locale.
 */
export function assertNoMissingKeys(locales: readonly SupportedLocale[] = SUPPORTED_LOCALES): void {
  const refLocale: SupportedLocale = DEFAULT_LOCALE;
  // Non-null assertions are safe here because BUNDLED_RESOURCES has a key for
  // every SupportedLocale as its type declares.
  const refResources: LocaleResources = BUNDLED_RESOURCES[refLocale]!;
  const errors: string[] = [];

  for (const locale of locales) {
    if (locale === refLocale) continue;

    const localeResources: LocaleResources = BUNDLED_RESOURCES[locale]!;

    for (const ns of Object.keys(refResources)) {
      const refKeys = collectLeafKeys((refResources[ns] ?? {}) as Record<string, unknown>);
      const locKeys = collectLeafKeys((localeResources[ns] ?? {}) as Record<string, unknown>);

      const missing = refKeys.filter((k) => !locKeys.includes(k));
      const extra = locKeys.filter((k) => !refKeys.includes(k));

      if (missing.length > 0) {
        errors.push(`[${locale}/${ns}] Missing keys: ${missing.join(', ')}`);
      }
      if (extra.length > 0) {
        errors.push(`[${locale}/${ns}] Extra keys not in 'en': ${extra.join(', ')}`);
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(`i18n key-parity failures:\n${errors.join('\n')}`);
  }
}

/**
 * Recursively collects all dot-notation leaf key paths from a nested object.
 * e.g. { a: { b: 'v' } } → ['a.b']
 */
function collectLeafKeys(obj: Record<string, unknown>, prefix = ''): string[] {
  const keys: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      keys.push(...collectLeafKeys(v as Record<string, unknown>, path));
    } else {
      keys.push(path);
    }
  }
  return keys;
}
